import React, { useState } from "react";
import { X } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAddNewPatientMutation } from "../../State/Api/PharmacySaleApiSlice";
import { Save } from "lucide-react";


export default function AddPatientModal({ open, onClose, onSave,isEdit }) {

  const [addNewPatient] = useAddNewPatientMutation();

  function computeAgeFromDOB(dobStr) {
  if (!dobStr) return { years: 0, months: 0 };
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return { years: 0, months: 0 };
  const today = new Date();

  let years = today.getFullYear() - dob.getFullYear();
  let months = today.getMonth() - dob.getMonth();

  if (today.getDate() < dob.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return { years: 0, months: 0 };
  return { years, months };
}

const phoneRegExp = /^\d{10}$/;

const validationSchema = Yup.object({
  patientName: Yup.string().trim().min(2, "Too short").required("Required"),
  mobileNumber: Yup.string()
    .matches(phoneRegExp, "Enter 10 digits")
    .required("Required"),
  gender: Yup.string().oneOf(["Male", "Female", "Other"], "Invalid").required("Required"),
  dateOfBirth: Yup.string()
    .test("valid-date", "Invalid date", (v) => !v || !Number.isNaN(Date.parse(v)))
    .test("not-future", "Cannot be in the future", (v) => !v || new Date(v) <= new Date()),
  ageYears: Yup.number().min(0, "Invalid").max(120, "Too large").required("Required"),
  ageMonths: Yup.number().min(0, "Invalid").max(11, "0-11").required("Required"),
  salesDiscount: Yup.number().min(0, "0-100").max(100, "0-100").required("Required"),
  referenceNo: Yup.string().trim(),
  address: Yup.string().trim().required("Required"),
});

const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      patientID:  0,
      pharmaID:  localStorage.getItem("userUniqueID"),
      patientName:  "",
      mobileNumber:  "",
      gender:  "",
      dateOfBirth:  "",
      ageYears:  0,
      ageMonths:  0,
      salesDiscount: 0,
      referenceNo: "",
      address: "",
    },
    validationSchema,
    onSubmit: async (values) => {

      try{
        const result = addNewPatient(values).unwrap();
        onSave({
          customerId: result.patientID,
          patientName: values.patientName,
        });
        formik.resetForm();
        onClose();
      }catch(e){
        console.log(e);
      }

    },
  });

  const [patient, setPatient] = useState({
    name: "",
    mobile: "",
    gender: "",
    relation: "",
    address: "",
    dob: "",
    ageYears: "",
    ageMonths: "",
    discount: "",
    refNo: "",
  });

  const err = (name) =>
  formik.touched[name] && formik.errors[name]
    ? <div className="mt-1 text-sm text-red-600">{formik.errors[name]}</div>
    : null;

  if (!open) return null;

  const handleSubmit = (e) => {
    formik.submitForm();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]"
    >
      <div
        className="bg-white rounded-2xl w-[95%] max-w-3xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
      >
        {/* Header */}
      
        <div className="flex justify-between items-center p-3 border-b">
          <div>
           <h4 className="font-bold text-lg text-gray-900">
  {isEdit ? "Edit Patient" : "Add New Patient"}
</h4>

<p className="text-sm text-gray-500">
  {isEdit ? "Update patient details below" : "Fill in the details below to add"}
</p>

          </div>

          <div className="flex items-center gap-2">
            
            <button
              onClick={onClose}
              style={{"background":"rgb(254, 226, 226)","border":"none","borderRadius":"6px","width":"36px","height":"36px","cursor":"pointer","display":"flex","alignItems":"center","justifyContent":"center"}}
            >
              <X size={18} color="#dc2626" />
            </button>
          </div>
        </div>

        {/* Form Body */}
        <form id="addPatientForm" onSubmit={handleSubmit} style={{ padding: "24px", overflowY: "auto" }} noValidate>
        <div className="p-6 overflow-y-auto space-y-4">
          {/* Row 1: Name + Mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-semibold text-sm text-gray-800 block mb-1">
                PATIENT'S NAME<span className="text-red-500">*</span>
              </label>
              <input
              name="patientName"
              value={formik.values.patientName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter Full Name here"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
              type="text"
              />
              {err("patientName")}
            </div>
            <div>
              <label className="font-semibold text-sm text-gray-800 flex justify-between mb-1">
                MOBILE NUMBER 
              </label>
              <input
  name="mobileNumber"
  value={formik.values.mobileNumber}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  type="tel"
  placeholder="Enter Mobile no here"
  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
  inputMode="numeric"
/>
              {err("mobileNumber")}
            </div>
          </div>

          {/* Gender */}
          <div className="flex items-center gap-5 border border-gray-300 rounded-lg px-4 py-2 -mt-2">
          <span className="font-semibold text-sm text-gray-800">GENDER IS</span>
          {["Male", "Female", "Other"].map((g) => (
            <label key={g} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="gender"
                value={g}
                checked={formik.values.gender === g}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="accent-green-600"
              />
              {g.toUpperCase()}
            </label>
          ))}
          {err("gender")}
        </div>



          {/* DOB + Age */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-semibold text-sm text-gray-800 flex justify-between mb-1">
                DATE OF BIRTH 
              </label>
              <input
                name="dateOfBirth"
  value={formik.values.dateOfBirth}
  onChange={(e) => {
    formik.handleChange(e);
    const { years, months } = computeAgeFromDOB(e.target.value);
    formik.setFieldValue("ageYears", years);
    formik.setFieldValue("ageMonths", months);
  }}
  onBlur={formik.handleBlur}
  type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
              />
              {err("dateOfBirth")}
            </div>
            <div>
              <label className="font-semibold text-sm text-gray-800 flex justify-between mb-1">
                PATIENT'S AGE 
              </label>
              <div className="flex gap-3">
                <input
                 name="ageYears"
  value={formik.values.ageYears}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  type="number"
  min="0"
  max="120"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
                />
                {err("ageYears")}
                <input
                  name="ageMonths"
  value={formik.values.ageMonths}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  type="number"
  min="0"
  max="11"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
                />
                {err("ageMonths")}
              </div>
            </div>
          </div>

          {/* Discount + Reference */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-semibold text-sm text-gray-800 flex justify-between mb-1">
                SALES DISCOUNT 
              </label>
              <div className="flex items-center gap-2">
                <input
                  name="salesDiscount"
  value={formik.values.salesDiscount}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  type="number"
  min="0"
  max="100"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
                />
                <span className="font-semibold text-gray-800">%</span>
              </div>
            </div>
            <div>
              <label className="font-semibold text-sm text-gray-800 flex justify-between mb-1">
                REFERENCE NO 
              </label>
              <input
                name="referenceNo"
  value={formik.values.referenceNo}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  type="text"
                placeholder="Enter Reference No here"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="font-semibold text-sm text-gray-800 flex justify-between mb-1">
              PATIENT'S ADDRESS 
            </label>
            <input
              name="address"
  value={formik.values.address}
  onChange={formik.handleChange}
  onBlur={formik.handleBlur}
  rows={3}
              placeholder="Enter Address here"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 font-medium"
            />
            {err("address")}
          </div>
        </div>
        <button
              type="button"
              className="gradient-button px-4 py-2 rounded-lg text-white font-semibold transition ml-auto mr-5"
              style={{display:"flex", alignItems:"center", gap:"5px"}}
              onClick={handleSubmit}
            >
              <Save size={16} />
             {isEdit ? "Update" : "Save"}
        </button>
        </form>
      </div>
    </div>
  );
}
